import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../Supabase/SupabaseClient';
import BrandArrow from '../ui/BrandArrow';
import './ServiceShowcase.css';
import Footer from '../Footer/Footer';

function Toast({ toasts, removeToast }) {
  return (
    <div className="boda-toast-wrapper" aria-live="polite" aria-atomic="true">
      {toasts.map((t) => (
        <div key={t.id} className={`boda-toast ${t.type || 'info'}`} role="status">
          <div className="boda-toast-message">{t.message}</div>
          <button className="boda-toast-close" aria-label="Cerrar" onClick={() => removeToast(t.id)}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function ServiceShowcase({ servicio, galeria = [], incluye = [] }) {
  // Solo se acepta la fila de `servicios`; las páginas viejas que pasaban un
  // string quedan en estado vacío en vez de renderizar a medias.
  const row = servicio && typeof servicio === 'object' ? servicio : null;

  const [addedToCart, setAddedToCart] = useState(false);
  const [agregando, setAgregando] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(1);
  const observerRef = useRef(null);
  const thumbsTrackRef = useRef(null);
  const [activeMedia, setActiveMedia] = useState(0);

  const nombre = row?.nombre || '';
  const jsonldId = `svc-jsonld-${row?.slug || row?.id || 'servicio'}`;
  const media = galeria.length
    ? galeria
    : [{ tipo: 'imagen', label: nombre, src: '/images/hero.png' }];

  useEffect(() => {
    setActiveMedia(0);
  }, [jsonldId]);

  useEffect(() => {
    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('boda-visible');
      });
    };
    observerRef.current = new IntersectionObserver(callback, { threshold: 0.12 });

    const els = document.querySelectorAll('.boda-fade-in');
    els.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (!observerRef.current) return;
      const elsCleanup = document.querySelectorAll('.boda-fade-in');
      elsCleanup.forEach((el) => observerRef.current.unobserve(el));
      observerRef.current.disconnect();
    };
  }, [row?.id]);

  useEffect(() => {
    const existing = document.getElementById(jsonldId);
    if (existing) existing.remove();
    if (!row) return;

    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: row.nombre,
      description: (row.descripcion || '').slice(0, 200),
      offers: {
        '@type': 'Offer',
        price: row.precio ? row.precio.toString() : '0',
        priceCurrency: 'USD',
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = jsonldId;
    script.appendChild(document.createTextNode(JSON.stringify(ld)));
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(jsonldId);
      if (el) el.remove();
    };
  }, [row, jsonldId]);

  // Verificar si el servicio ya está en el carrito
  const checkCartStatus = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getUser();
      if (!data?.user) {
        setAddedToCart(false);
        return;
      }

      const { data: cartItems, error } = await supabase
        .from('carrito')
        .select('servicio_id')
        .eq('usuario_id', data.user.id);

      if (error) {
        console.error('Error al verificar el carrito:', error);
        return;
      }

      setAddedToCart(
        (cartItems || []).some(
          (item) => String(item.servicio_id) === String(row.id)
        )
      );
    } catch (err) {
      console.error('Error al verificar estado del carrito:', err);
    }
  }, [row]);

  useEffect(() => {
    const handleCartUpdate = () => checkCartStatus();

    document.addEventListener('cartUpdate', handleCartUpdate);
    document.addEventListener('cartItemRemoved', handleCartUpdate);
    document.addEventListener('cartCleared', handleCartUpdate);

    checkCartStatus();

    return () => {
      document.removeEventListener('cartUpdate', handleCartUpdate);
      document.removeEventListener('cartItemRemoved', handleCartUpdate);
      document.removeEventListener('cartCleared', handleCartUpdate);
    };
  }, [checkCartStatus]);

  const scrollThumbs = (dir) => {
    const track = thumbsTrackRef.current;
    if (!track) return;
    const thumb = track.querySelector('.boda-thumb');
    const step = thumb ? thumb.getBoundingClientRect().width + 14 : 140;
    track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const pushToast = (message, type = 'info', timeout = 4200) => {
    const id = toastIdRef.current++;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (timeout > 0) setTimeout(() => removeToast(id), timeout);
  };
  const removeToast = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const dispatchCartUpdateEvent = () => {
    const cartEvent = new CustomEvent('cartUpdate', { detail: { showModal: true } });
    document.dispatchEvent(cartEvent);
    const updateEvent = new CustomEvent('cartChanged');
    document.dispatchEvent(updateEvent);
  };

  const handleAddToCart = async () => {
    if (!row) return;

    setAgregando(true);
    try {
      const { data: session } = await supabase.auth.getUser();
      if (!session?.user) {
        pushToast('Iniciá sesión para agregar servicios', 'error');
        return;
      }

      const { data: existingCartItems, error: checkError } = await supabase
        .from('carrito')
        .select('*')
        .eq('usuario_id', session.user.id);

      if (checkError) throw checkError;

      const existente = (existingCartItems || []).find(
        (item) => String(item.servicio_id) === String(row.id)
      );

      if (existente) {
        const { error: updateError } = await supabase
          .from('carrito')
          .update({ cantidad: (existente.cantidad || 1) + 1 })
          .eq('id', existente.id);
        if (updateError) throw updateError;

        dispatchCartUpdateEvent();
        pushToast('Cantidad actualizada en el carrito', 'success');
      } else {
        const { error: insertError } = await supabase
          .from('carrito')
          .insert({ usuario_id: session.user.id, servicio_id: row.id, cantidad: 1 });
        if (insertError) throw insertError;

        dispatchCartUpdateEvent();
        pushToast('Servicio añadido al carrito', 'success');
      }

      setAddedToCart(true);
    } catch (err) {
      console.error('Error al añadir al carrito:', err);
      pushToast('No se pudo añadir el servicio al carrito', 'error');
    } finally {
      setAgregando(false);
    }
  };

  if (!row) {
    return <div className="boda-empty-container">No se encontró el servicio solicitado.</div>;
  }

  return (
    <div className="boda-wrapper">
      {/* ── Vitrina: media grande + miniaturas | info + qué incluye ── */}
      <section className="boda-showcase" aria-label="Galería y servicio destacado">
        <div className="boda-showcase-media boda-fade-in">
          <div className="boda-showcase-main">
            {media[activeMedia].tipo === 'video' ? (
              <iframe
                title={media[activeMedia].label}
                src={media[activeMedia].src}
                allow="autoplay; encrypted-media"
                allowFullScreen
                className="boda-showcase-video"
              />
            ) : (
              <img
                src={media[activeMedia].src}
                alt={media[activeMedia].label}
                className="boda-showcase-image"
              />
            )}
          </div>

          <div className="boda-thumbs-carousel">
            <button
              className="boda-thumbs-arrow boda-thumbs-arrow--prev"
              onClick={() => scrollThumbs(-1)}
              aria-label="Ver anteriores"
            >
              <BrandArrow direction="left" className="boda-carousel-icon" />
            </button>
            <div className="boda-thumbs-track" ref={thumbsTrackRef}>
              {media.map((item, index) => (
                <button
                  key={item.src || index}
                  type="button"
                  onClick={() => setActiveMedia(index)}
                  className={`boda-thumb ${activeMedia === index ? 'boda-thumb--active' : ''}`}
                  aria-current={activeMedia === index}
                  aria-label={item.label}
                  title={item.label}
                >
                  {item.tipo === 'video' ? (
                    <span className="boda-thumb-video" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  ) : (
                    <img src={item.src} alt={item.label} loading="lazy" />
                  )}
                </button>
              ))}
            </div>
            <button
              className="boda-thumbs-arrow boda-thumbs-arrow--next"
              onClick={() => scrollThumbs(1)}
              aria-label="Ver siguientes"
            >
              <BrandArrow direction="right" className="boda-carousel-icon" />
            </button>
          </div>
        </div>

        <aside className="boda-showcase-info boda-fade-in">
          <h2 className="boda-showcase-title">{row.nombre}</h2>
          {row.nota && <p className="boda-showcase-desc">{row.nota}</p>}
          <p className="boda-showcase-desc">{row.descripcion || ''}</p>

          <div className="boda-showcase-price">
            {row.precio ? `USD$${row.precio}` : 'Consultar'}
          </div>

          <button
            onClick={handleAddToCart}
            disabled={agregando}
            className={`boda-showcase-btn ${addedToCart ? 'boda-showcase-btn--added' : ''}`}
            aria-pressed={addedToCart}
          >
            <span>
              {addedToCart ? 'AÑADIDO AL CARRITO' : agregando ? 'AGREGANDO…' : 'AÑADIR AL CARRITO'}
            </span>
            <span className="boda-showcase-btn-arrow" aria-hidden="true">
              <BrandArrow />
            </span>
          </button>
        </aside>

        <div className="boda-showcase-incluye boda-showcase-incluye--full boda-fade-in">
          <div className="boda-showcase-incluye-ttl">
            <h3 className="boda-showcase-incluye-title">
              ¿Qué incluye?
              <BrandArrow direction="down-right" className="boda-showcase-incluye-arrow" />
            </h3>
          </div>
          <ul className="boda-showcase-incluye-lista">
            {incluye.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
